// 用户角色枚举
export enum UserRole {
  DISEASE_CONTROL = 'disease_control', // 卡若区疾控中心
  SCHOOL_MEDICAL = 'school_medical',   // 学校医务室
  COMMUNITY = 'community',             // 社区检查点
  DOCTOR = 'doctor'                    // 医生
}

// 用户接口
export interface User {
  id: string;
  username: string;
  name: string;
  role: UserRole;
  roleId?: string; // 角色ID
  organization?: string; // 所属机构名称
  organizationId?: string; // 所属机构ID
  avatar?: string;
  phone?: string;
  email?: string;
  isProvider?: boolean;
  businessLicense?: string;
  idCardFront?: string;
  idCardBack?: string;
  serviceCategoryIds?: string;
  orderSourceWechat?: boolean;
  orderSourceMeituan?: boolean;
  wechatServiceRegions?: string;
  meituanShopIds?: string;
  withdrawableAmount?: number;
  status: 'active' | 'inactive';
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  permissions?: string[]; // 用户权限列表
}

// 登录表单数据
export interface LoginForm {
  username: string;
  password: string;
  captcha: string;
}

// 登录响应
export interface LoginResponse {
  token: string;
  user: User;
}

// 视力筛查数据接口
export interface VisionData {
  id: string;
  studentName: string;
  studentId: string;
  schoolName: string;
  schoolId: string;
  grade: string;
  class: string;
  gender: 'male' | 'female';
  age: number;
  birthday: string;
  checkDate: string;
  leftVision: number; // 左眼视力
  rightVision: number; // 右眼视力
  leftSph: number; // 左眼球镜度数
  leftCyl: number; // 左眼柱镜度数
  leftAxis: number; // 左眼轴位
  rightSph: number; // 右眼球镜度数
  rightCyl: number; // 右眼柱镜度数
  rightAxis: number; // 右眼轴位
  isAbnormal: boolean;
  abnormalType?: string;
  suggestion?: string;
  recorder: string;
  createdAt: string;
  updatedAt: string;
}

// 健康监测数据接口
export interface HealthData {
  id: string;
  residentName: string;
  residentId: string;
  communityName: string;
  communityId: string;
  gender: 'male' | 'female';
  age: number;
  birthday: string;
  checkDate: string;
  bloodPressure: {
    systolic: number;    // 收缩压
    diastolic: number;   // 舒张压
  };
  bloodSugar: {
    fasting?: number;    // 空腹血糖
    postprandial?: number; // 餐后血糖
  };
  bloodLipid: {
    cholesterol?: number; // 总胆固醇
    triglyceride?: number; // 甘油三酯
    hdl?: number;         // 高密度脂蛋白
    ldl?: number;         // 低密度脂蛋白
  };
  heartRate: number; // 心率
  height?: number;   // 身高
  weight?: number;   // 体重
  bmi?: number;      // BMI指数
  isAbnormal: boolean;
  abnormalIndicators?: string[];
  suggestion?: string;
  recorder: string;
  createdAt: string;
  updatedAt: string;
}

// 数据查询参数
export interface QueryParams {
  type: 'vision' | 'health';
  dateRange?: [string, string];
  ageRange?: [number, number];
  gender?: 'male' | 'female' | 'all';
  organizationId?: string;
  isAbnormal?: boolean;
  keyword?: string;
  page: number;
  pageSize: number;
}

// 查询响应
export interface QueryResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

// 数据分析选项
export interface AnalysisOptions {
  type: 'vision' | 'health';
  dimension: 'gender' | 'age' | 'organization' | 'time';
  dateRange: [string, string];
  groupBy?: string;
}

// 仪表盘统计卡片数据
export interface DashboardCard {
  title: string;
  value: number;
  prefix?: string;
  suffix?: string;
  trend?: {
    value: number;
    isUp: boolean;
  };
  icon?: React.ReactNode;
}

// 图表数据点
export interface ChartDataPoint {
  name: string;
  value: number;
  rate?: number;
}

// 趋势数据点
export interface TrendDataPoint {
  date: string;
  value: number;
  category?: string;
}

// API 响应通用格式
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  code: number;
}

// 分页参数
export interface PaginationParams {
  current: number;
  pageSize: number;
  total?: number;
}

// 表格列配置
export interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
  width?: number;
  render?: (value: any, record: any, index: number) => React.ReactNode;
  sorter?: boolean;
  filterable?: boolean;
  align?: 'left' | 'center' | 'right';
}

// 导出选项
export interface ExportOptions {
  type: 'excel' | 'pdf';
  fields: string[];
  filename?: string;
}

// 系统设置
export interface SystemSettings {
  siteName: string;
  logo: string;
  version: string;
  maintenance: boolean;
  announcement?: string;
}

// 疫苗类型
export enum VaccineType {
  HEPATITIS_B = 'hepatitis_b', // 乙肝疫苗
  HEPATITIS_A = 'hepatitis_a', // 甲肝疫苗
  MEASLES = 'measles', // 麻疹疫苗
  MMR = 'mmr', // 麻腮风疫苗
  VARICELLA = 'varicella', // 水痘疫苗
  CHICKENPOX = 'chickenpox', // 带状疱疹疫苗
  PNEUMOCOCCAL = 'pneumococcal', // 肺炎疫苗
  MENINGOCOCCAL = 'meningococcal', // 脑膜炎疫苗
  INFLUENZA = 'influenza', // 流感疫苗
  HPV = 'hpv', // HPV疫苗
  COVID19 = 'covid19', // 新冠疫苗
  RABIES = 'rabies', // 狂犬病疫苗
  YELLOW_FEVER = 'yellow_fever', // 黄热病疫苗
  JAPANESE_ENCEPHALITIS = 'japanese_encephalitis', // 乙脑疫苗
  DTP = 'dtp', // 百白破疫苗
  OTHER = 'other' // 其他疫苗
}

// 疫苗注射计划状态
export enum VaccinePlanStatus {
  PENDING = 'pending', // 待开始
  IN_PROGRESS = 'in_progress', // 进行中
  COMPLETED = 'completed', // 已完成
  CANCELLED = 'cancelled' // 已取消
}

// 疫苗针剂记录
export interface VaccineDose {
  id: string;
  doseNumber: number; // 针剂序号
  plannedDate: string; // 计划注射日期
  actualDate?: string; // 实际注射日期
  status: 'pending' | 'completed' | 'missed'; // 状态
  sideEffects?: string; // 不良反应
  notes?: string; // 备注
  administeredBy?: string; // 注射医生
  batchNumber?: string; // 疫苗批号
  manufacturer?: string; // 生产厂家
  createdAt: string;
  updatedAt: string;
}

// 疫苗注射计划
export interface VaccinePlan {
  id: string;
  userId: string; // 用户ID
  userName: string; // 用户姓名
  userGender: 'male' | 'female';
  userBirthday: string; // 出生日期
  userAge: number; // 年龄
  userPhone?: string; // 联系电话
  userAddress?: string; // 地址
  vaccineType: VaccineType; // 疫苗类型
  vaccineName: string; // 疫苗名称（自定义名称）
  totalDoses: number; // 总针剂数
  doses: VaccineDose[]; // 针剂列表
  status: VaccinePlanStatus; // 计划状态
  startDate: string; // 开始日期
  endDate?: string; // 预计完成日期
  completedDate?: string; // 实际完成日期
  doctorId: string; // 负责医生ID
  doctorName: string; // 负责医生姓名
  organizationId: string; // 机构ID
  organizationName: string; // 机构名称
  notes?: string; // 备注
  createdAt: string;
  updatedAt: string;
}

// 疫苗计划表单数据
export interface VaccinePlanFormData {
  userId: string;
  userName: string;
  userGender: 'male' | 'female';
  userBirthday: string;
  userPhone?: string;
  userAddress?: string;
  vaccineType: VaccineType;
  vaccineName: string;
  totalDoses: number;
  doses: Omit<VaccineDose, 'id' | 'createdAt' | 'updatedAt'>[];
  startDate: string;
  endDate?: string;
  notes?: string;
}

// 积分记录类型
export interface PointsRecord {
  id: string;
  userId: string;
  userName: string;
  vaccinePlanId: string;
  vaccineName: string;
  doseNumber: number;
  points: number;
  reason: string;
  date: string;
  createdAt: string;
}
